// The fix-state transition API (shift-engine §1; contracts.md Slice-1 row).
//
// The MACHINE that advances a fix through the lifecycle in model/fix-state.ts. Types
// only here; the guarded transition logic lands in @nightcrew/shift (Wave 3). Each
// transition is driven by a `ShiftEvent` and evaluated against a `GuardCtx`. A failed
// guard never silently advances: the logic moves the fix to `escalated`/`blocked` (a
// valid FixState result), while an event that does not apply to the current state is an
// `illegal_transition` TransitionError (fail closed).
import { z } from "zod";
import { NonEmptyString } from "../model/common.js";
import { FixCategory, FixRisk } from "../model/enums.js";
/** Classification carried by the `triage` event; mirrors the FixState `classified` fields. */
const classified = {
    category: FixCategory,
    risk: FixRisk,
    touchesHardGate: z.boolean(),
};
/**
 * The events that drive the fix state machine, discriminated on `type`. The happy-path
 * events (triage -> ... -> complete_retro) mirror the forward edges; the side events
 * (escalate/block/roll_back/fail) and the build-flow pause (await/resume_manual_step)
 * carry their own payloads. A Zod union so an event parses at the boundary and the set
 * is provably exhaustive.
 */
export const ShiftEvent = z.discriminatedUnion("type", [
    z.strictObject({ type: z.literal("triage"), ...classified }),
    z.strictObject({ type: z.literal("research") }),
    z.strictObject({ type: z.literal("implement") }),
    z.strictObject({ type: z.literal("test") }),
    z.strictObject({ type: z.literal("stage"), previewUrl: z.url() }),
    z.strictObject({ type: z.literal("gate") }),
    z.strictObject({ type: z.literal("request_tap") }),
    z.strictObject({ type: z.literal("release"), prUrl: z.url() }),
    z.strictObject({ type: z.literal("mark_released"), resolvedAt: z.iso.datetime() }),
    z.strictObject({ type: z.literal("complete_retro") }),
    z.strictObject({ type: z.literal("await_manual_step") }),
    z.strictObject({ type: z.literal("resume_manual_step") }),
    z.strictObject({ type: z.literal("escalate"), reason: NonEmptyString }),
    z.strictObject({ type: z.literal("block"), reason: NonEmptyString }),
    z.strictObject({ type: z.literal("roll_back"), reason: NonEmptyString, prUrl: z.url() }),
    z.strictObject({ type: z.literal("fail"), reason: NonEmptyString }),
]);
/** Every event discriminant, in lifecycle order. `satisfies` proves each is a real type. */
export const SHIFT_EVENT_TYPES = [
    "triage",
    "research",
    "implement",
    "test",
    "stage",
    "gate",
    "request_tap",
    "release",
    "mark_released",
    "complete_retro",
    "await_manual_step",
    "resume_manual_step",
    "escalate",
    "block",
    "roll_back",
    "fail",
];
//# sourceMappingURL=shift.js.map